import React from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Button, cn } from "@ndd/ui";
import { RD_HOURS_COPY, timeZoneOptions } from "../../../lib/rd-hours-client";
import type { RdHoursMode } from "../../../lib/rd-hours-draft";
import { FOCUS } from "./look";

export interface RdHoursToolbarProps {
  mode: RdHoursMode;
  onMode: (mode: RdHoursMode) => void;
  /** "Nov 2 to 8, 2026"; null in Every week. */
  weekLabel: string | null;
  canPrevious: boolean;
  onPreviousWeek: () => void;
  onNextWeek: () => void;
  timeZone: string;
  onTimeZone: (timeZone: string) => void;
  /** One week: the week save carries no zone, so the select is read-only. */
  zoneDisabled: boolean;
}

const MODES: readonly { mode: RdHoursMode; label: string }[] = [
  { mode: "template", label: "Every week" },
  { mode: "week", label: "One week" },
];

function ModeToggle({
  label,
  pressed,
  onClick,
}: {
  label: string;
  pressed: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "h-8 rounded-md border border-transparent px-3 text-body-lg font-semibold",
        FOCUS,
        pressed
          ? "bg-secondary text-foreground"
          : "bg-transparent text-muted-foreground"
      )}
    >
      {label}
    </button>
  );
}

function WeekArrows({
  weekLabel,
  canPrevious,
  onPreviousWeek,
  onNextWeek,
}: Pick<
  RdHoursToolbarProps,
  "weekLabel" | "canPrevious" | "onPreviousWeek" | "onNextWeek"
>) {
  return (
    <span className="flex items-center gap-1.5">
      <Button
        type="button"
        variant="utility"
        className="size-8 rounded-md p-0"
        aria-label="Previous week"
        disabled={!canPrevious}
        onClick={onPreviousWeek}
      >
        <ChevronLeft aria-hidden="true" className="!size-[15px]" />
      </Button>
      <span className="font-sans text-reading font-bold">{weekLabel}</span>
      <Button
        type="button"
        variant="utility"
        className="size-8 rounded-md p-0"
        aria-label="Next week"
        onClick={onNextWeek}
      >
        <ChevronRight aria-hidden="true" className="!size-[15px]" />
      </Button>
    </span>
  );
}

function TimeZoneSelect({
  timeZone,
  onTimeZone,
  disabled,
}: {
  timeZone: string;
  onTimeZone: (timeZone: string) => void;
  disabled: boolean;
}) {
  const noteId = React.useId();

  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="relative inline-flex items-center">
        <select
          aria-label="Time zone"
          aria-describedby={disabled ? noteId : undefined}
          value={timeZone}
          disabled={disabled}
          onChange={(event) => onTimeZone(event.target.value)}
          className={cn(
            "h-9 appearance-none rounded-md border border-(color:--button-utility-border,var(--input)) bg-card py-0 pl-3 pr-9 text-reading font-semibold text-foreground disabled:cursor-not-allowed disabled:opacity-60",
            FOCUS
          )}
        >
          {timeZoneOptions(timeZone).map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden="true"
          className="pointer-events-none absolute right-3 size-[15px] text-muted-foreground"
        />
      </span>
      {disabled ? (
        <span id={noteId} className="text-caption text-muted-foreground">
          {RD_HOURS_COPY.zoneInWeek}
        </span>
      ) : null}
    </span>
  );
}

export function RdHoursToolbar(props: RdHoursToolbarProps) {
  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
      <div role="group" aria-label="Which hours to edit" className="flex gap-1">
        {MODES.map(({ mode, label }) => (
          <ModeToggle
            key={mode}
            label={label}
            pressed={props.mode === mode}
            onClick={() => props.onMode(mode)}
          />
        ))}
      </div>
      {props.mode === "week" && props.weekLabel !== null ? (
        <WeekArrows
          weekLabel={props.weekLabel}
          canPrevious={props.canPrevious}
          onPreviousWeek={props.onPreviousWeek}
          onNextWeek={props.onNextWeek}
        />
      ) : null}
      <span className="grow" />
      <TimeZoneSelect
        timeZone={props.timeZone}
        onTimeZone={props.onTimeZone}
        disabled={props.zoneDisabled}
      />
    </div>
  );
}
